import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { api, errorText } from "@/lib/api";
import { PasswordInput } from "@/components/PasswordInput";
import { toast } from "sonner";

const HERO = "https://images.unsplash.com/photo-1660557989695-14fac79c086d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function AuthPage({ mode }) {
  const isLogin = mode === "login";
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [sent, setSent] = useState("");
  const requestReset = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: form.email });
      setSent(data.message);
      toast.success("Reset request sent");
    } catch (err) { setError(errorText(err)); } finally { setBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (isLogin) await login(form.email, form.password);
      else await register(form.name, form.email, form.password);
      navigate("/");
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden lg:block overflow-hidden grain bg-slate-900">
        <img src={HERO} alt="Hotel" className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-end p-14 text-white">
          <p className="text-xs tracking-[0.3em] uppercase text-emerald-300 mb-4">Armella Group · USALI Budget Tools</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] max-w-xl">
            Next year's budget, built from your properties' actual data.
          </h1>
          <p className="mt-6 text-slate-300 max-w-lg text-base">
            Upload P&amp;L reports, set minimum wage, inflation, manning and occupancy assumptions, then download the USALI budget as Excel &amp; PDF.
          </p>
        </div>
      </div>
      <div className="flex items-center justify-center px-6 py-12">
        {forgot ? (
        <form onSubmit={requestReset} className="w-full max-w-md rise" data-testid="forgot-password-form">
          <img src="/armella-logo.png" alt="Armella" className="h-10 w-auto mb-8" />
          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Forgot your password?</h2>
          <p className="text-sm text-slate-500 mt-2 mb-8">Enter your email. The Armella super admin will be notified and will issue you a temporary password.</p>
          <div className="space-y-5">
            <div className="space-y-2"><Label htmlFor="femail">Email</Label><Input id="femail" type="email" data-testid="forgot-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required placeholder="you@armella.co.id" /></div>
            {sent && <p className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg p-3" data-testid="forgot-success">{sent}</p>}
            {error && <p className="text-sm text-red-600" data-testid="auth-error">{error}</p>}
            {!sent && <Button type="submit" disabled={busy} data-testid="forgot-submit-button" className="w-full h-11 bg-slate-900 hover:bg-emerald-700 text-white">{busy ? "Sending..." : "Request password reset"}</Button>}
            <button type="button" onClick={() => setForgot(false)} className="w-full text-sm text-slate-600 hover:text-slate-900" data-testid="back-to-login-link">← Back to sign in</button>
          </div>
        </form>
        ) : (
        <form onSubmit={submit} className="w-full max-w-md rise" data-testid={`${mode}-form`}>
          <div className="mb-10">
            <img src="/armella-logo.png" alt="Armella" className="h-10 w-auto" data-testid="armella-logo" />
            <p className="text-[11px] uppercase tracking-[0.25em] text-slate-500 font-semibold mt-3">USALI Budget Tools</p>
          </div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
            {isLogin ? "Sign in to the workspace" : "Create a new account"}
          </h2>
          <p className="text-sm text-slate-500 mt-2 mb-8">
            {isLogin ? "Use the email and password issued by your administrator." : "Bootstrap the first super admin account."}
          </p>
          <div className="space-y-5">
            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input id="name" data-testid="register-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="Full name" />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid={`${mode}-email-input`} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required placeholder="finance@armella.co.id" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <PasswordInput id="password" data-testid={`${mode}-password-input`} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} placeholder="At least 6 characters" />
            </div>
            {isLogin && <div className="text-right -mt-2"><button type="button" onClick={() => { setForgot(true); setError(""); setSent(""); }} className="text-xs text-emerald-700 hover:underline" data-testid="forgot-password-link">Forgot password?</button></div>}
            {error && <p className="text-sm text-red-600" data-testid="auth-error">{error}</p>}
            <Button type="submit" disabled={busy} data-testid={`${mode}-submit-button`} className="w-full h-11 bg-slate-900 hover:bg-emerald-700 text-white transition-colors">
              {busy ? "Processing..." : isLogin ? "Sign in" : "Register"} <ArrowRight size={16} className="ml-2" />
            </Button>
          </div>
          <p className="text-xs text-slate-500 mt-8" data-testid="auth-closed-notice">
            Access is restricted to registered Armella Group users. Need an account or a password reset? Contact your super admin.
          </p>
        </form>
        )}
      </div>
    </div>
  );
}
